import React from 'react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import styles from './UsageTracker.module.scss';

interface UsageTrackerProps {
  showUpgradeButton?: boolean;
  compact?: boolean;
}

export const UsageTracker: React.FC<UsageTrackerProps> = ({
  showUpgradeButton = true,
  compact = false,
}) => {
  const {
    subscriptionStatus,
    getUsagePercentage,
    getRemainingGenerations,
    canGenerateCourse,
  } = useSubscription();
  const navigate = useNavigate();

  if (!subscriptionStatus) {
    return null;
  }

  const { usage, subscriptionTier } = subscriptionStatus;
  const usagePercentage = getUsagePercentage();
  const remainingGenerations = getRemainingGenerations();

  const getUsageColor = () => {
    if (usagePercentage >= 90) return '#ef4444'; // red
    if (usagePercentage >= 70) return '#f59e0b'; // amber
    return '#22c55e'; // green
  };

  const formatRemainingGenerations = () => {
    if (remainingGenerations === Infinity) return 'Unlimited';
    return remainingGenerations.toString();
  };

  if (compact) {
    return (
      <div className={styles.compactUsageTracker}>
        <div className={styles.compactInfo}>
          <span className={styles.remaining}>
            {formatRemainingGenerations()} left
          </span>
          {subscriptionTier !== 'unlimited' && (
            <div className={styles.compactProgressBar}>
              <div
                className={styles.compactProgressFill}
                style={{
                  width: `${usagePercentage}%`,
                  backgroundColor: getUsageColor(),
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.usageTracker}>
      <div className={styles.header}>
        <h3>Course Generation Usage</h3>
        <span className={styles.tier}>{subscriptionTier.toUpperCase()}</span>
      </div>

      {subscriptionTier === 'free' ? (
        <div className={styles.freeUsage}>
          <div className={styles.freeStatus}>
            <span className={styles.freeLabel}>Free Course:</span>
            <span
              className={`${styles.freeValue} ${usage.freeCourseUsed ? styles.used : styles.available}`}
            >
              {usage.freeCourseUsed ? 'Used' : 'Available'}
            </span>
          </div>
          {usage.freeCourseUsed && showUpgradeButton && (
            <button
              className={styles.upgradeButton}
              onClick={() => navigate('/upgrade-plan')}
            >
              Upgrade to Generate More
            </button>
          )}
        </div>
      ) : subscriptionTier === 'unlimited' ? (
        <div className={styles.unlimitedUsage}>
          <div className={styles.unlimitedIcon}>∞</div>
          <p>Unlimited course generations</p>
          <div className={styles.stats}>
            <span>Total generated: {usage.totalCoursesGenerated}</span>
          </div>
        </div>
      ) : (
        <div className={styles.paidUsage}>
          <div className={styles.usageStats}>
            <div className={styles.currentUsage}>
              <span className={styles.used}>
                {usage.coursesGeneratedThisPeriod}
              </span>
              <span className={styles.separator}>/</span>
              <span className={styles.limit}>{usage.monthlyLimit}</span>
              <span className={styles.label}>courses this month</span>
            </div>
            <div className={styles.remaining}>
              {remainingGenerations} remaining
            </div>
          </div>

          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{
                width: `${usagePercentage}%`,
                backgroundColor: getUsageColor(),
              }}
            />
          </div>

          {usage.currentPeriodEnd && (
            <div className={styles.resetInfo}>
              Resets on {new Date(usage.currentPeriodEnd).toLocaleDateString()}
            </div>
          )}

          {!canGenerateCourse() && showUpgradeButton && (
            <button
              className={styles.upgradeButton}
              onClick={() => navigate('/upgrade-plan')}
            >
              Upgrade Plan
            </button>
          )}
        </div>
      )}
    </div>
  );
};
